import AvailabilityProvider from "../services/AvailabilityProvider"
import Offer from '../models/Offer'
import DateService from "../services/DateService"

export class BookingTime {
    private startTime: StartTime
    private endTime?: EndTime
    duration: number

    constructor (startTime: StartTime, duration: number = -1) {
        this.startTime = startTime
        this.duration = duration

        if (this.duration > 0) {
            this.endTime = EndTime.createFromDuration(startTime.time, duration)
        }
    }

    getDuration (): number {
        return this.duration
    }

    getStartTimeForAPI (): string {
        return this.startTime.time.str
    }

    getFriendlyStartTime (): string {
        return this.startTime.friendlyTime()
    }

    getFriendlyEndTime (): string {
        if (this.duration === -1) {
            return EndTime.untilCloseStr
        }

        return this.endTime!.friendlyTime()
    }
}

export enum TimeAction {
    canAccept = 'can_accept',
    mayAccept = 'may_accept',
    accept = 'accept',
    confirm = 'confirm',
    reject = 'reject'
}

class Time {
    str: string
    hours: number
    minutes: number
    offers: any[] = []
    isCombined: boolean = false

    constructor (strTime: string) {
        let hours
        let minutes

        [hours, minutes] = strTime.split(':')

        this.hours = Number(hours)
        this.minutes = Number(minutes)
        this.str = strTime
    }
}

abstract class TimeSlot {
    isValid: boolean
    action: TimeAction
    message: string | null
    offers: Offer[] = []
    selected: boolean

    private offerIds: string[] = []

    static readonly pastErrorMessage: string = 'The time is in the past'

    constructor (data: any) {
        this.isValid = data.valid
        this.action = data.action
        this.message = data.message
        this.selected = false

        // Handle case where message can be an array of strings
        if (Array.isArray(this.message) && this.message.length) {
            // Just retrieve the first message
            this.message = this.message[0]
        }

        if (data.offers) {
            this.offerIds = data.offers
        }
    }

    get isInPast (): boolean {
        return !this.isValid && this.message === TimeSlot.pastErrorMessage
    }

    get isInstant (): boolean {
        return this.action === TimeAction.accept
    }

    get isDisabled (): boolean {
        return this.action === TimeAction.reject
    }

    get subtext (): string {
        switch (this.action) {
            case TimeAction.reject:
                return 'time_unavailable'
            case TimeAction.accept:
                return 'instant_book'
            default:
                return 'make_enquiry'
        }
    }

    get isBookable (): boolean {
        return this.isValid && this.action !== TimeAction.reject
    }

    fillOffers (offers: Offer[]): this {
        this.offers = offers.filter((offer) => {
            return this.offerIds.indexOf(offer.id) > -1
        })

        return this
    }
}

export class StartTime extends TimeSlot {
    time: Time

    constructor (data: any) {
        super(data)
        this.time = new Time(data.time)
    }

    get uniqueId (): string {
        return 'start:' + this.time.str + '_' + Math.random()
    }

    get subtext (): string {
        switch (this.action) {
            case TimeAction.reject:
                return 'time_unavailable'
            case TimeAction.accept:
                return 'instant_book'
            default:
                return ''
        }
    }

    friendlyTime () {
        return DateService.friendlyTime(this.time)
    }
}

class EndTime extends TimeSlot {
    time: Time
    duration: number
    static readonly untilCloseStr: string = 'Until Close'

    constructor (data: any) {
        super(data)
        this.time = new Time(data.endTime)
        this.duration = data.value
    }

    static createFromDuration (startTime: Time, duration: number) {
        const endDateTime = DateService.addMinutesToTime(duration, startTime)
        const endDateTimeStr = DateService.formatTimeForAPI(endDateTime)

        return new EndTime({
            endTime: endDateTimeStr,
            duration: duration
        })
    }

    get uniqueId (): string {
        return 'end:' + this.time.str
    }

    friendlyTime () {
        if (this.duration === -1) {
            return EndTime.untilCloseStr
        }

        return DateService.friendlyTime(this.time)
    }
}

export class MultiTypeTimeDetails {
  bookingType: string
  name: string
  startTimes: StartTime[]
  bufferBefore: number
  bufferAfter: number

  constructor (typeId: string, data: any) {
    this.bookingType = typeId
    this.name = data.name
    this.bufferBefore = data.buffers.before
    this.bufferAfter = data.buffers.after

    this.startTimes = []
    data.times.forEach((time: any) => {
      this.startTimes.push(new StartTime(time))
    })
  }
}

export class TimesProvider extends AvailabilityProvider {

    get offers (): Offer[] {
        return this.booking.venue ? this.booking.venue.offers : []
    }

    private buildBookingParams (timeFrom : any = null): { [index in string] : any } {
        return {
            type: this.booking.type!.id,
            num_people: this.booking.pax,
            date: DateService.formatDateForAPI(new Date(timeFrom ? timeFrom : this.booking.date!)),
            getOffers: true,
        }
    }

    requestStartTimes (timeFrom :Date, timeTo :Date): Promise<StartTime[]> {
        const url = this._buildUrl(this.booking.venue!)
        let params = this.buildBookingParams(timeFrom)

        if (timeFrom) {
          params.time_from =  `${DateService.formatTimeForAPI(timeFrom)}`
        }

        if (timeTo) {
          params.time_to = `${DateService.formatTimeForAPI(timeTo)}`
        }

        const requestUrl = url + this.objToParams(params)

        return this.get(requestUrl)
            .then((response) => {
                const payload = response.data.payload
                const startTimeValues = payload.validation.time.suggestedValues
                let startTimes: StartTime[] = []
                let startTime: StartTime

                startTimeValues.forEach((time: any) => {
                    startTime = new StartTime(time)
                    startTime.fillOffers(this.offers)

                    startTimes.push(startTime)
                })

                return startTimes
            })
    }

    requestEndTimes (startTime: StartTime): Promise<EndTime[]> {
        const url = this._buildUrl(this.booking.venue!)
        let params = this.buildBookingParams()

        params.time = startTime.time.str
        params.fields = 'duration'
        params.skipAltTimes = true

        const requestUrl = url + this.objToParams(params)

        return this.get(requestUrl)
            .then((response) => {
                const payload = response.data.payload
                const endTimeValues = payload.validation.duration.suggestedValues
                let endTimes: EndTime[] = []
                let endTime: EndTime

                endTimeValues.forEach((time: any) => {
                    endTime = new EndTime(time)
                    endTime.fillOffers(this.offers)

                    endTimes.push(endTime)
                })

                return endTimes
            })
    }

    requestTimesForMultipleTypes (additionalTypes: string[]) : Promise<MultiTypeTimeDetails[]> {
      const url = this._buildUrl(this.booking.venue!)
      let params = this.buildBookingParams()

      // Modify params to include multipleTypes (start with original booking type first to preserve ordering)
      params.multipleTypes = [params.type]

      additionalTypes.forEach((type) => {
        params.multipleTypes.push(type)
      })

      delete params.type;

      params.fields = 'multipleTimes'

      const requestUrl = url + this.objToParams(params)

      return this.get(requestUrl)
        .then((response) => {
          const payload = response.data.payload
          const multiTimeValues = payload.validation.multipleTimes.suggestedValues

          let typeTimes: MultiTypeTimeDetails[] = []

          Object.keys(multiTimeValues).forEach(key => {
            typeTimes.push(new MultiTypeTimeDetails(key, multiTimeValues[key]))
          });

          return typeTimes
        })
    }
}



// WEBPACK FOOTER //
// ./src/services/TimesProvider.ts